import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { CHECK_INTERVAL_MS, fetchDeployedBuild, isNewerBuild, runningBuild } from './newBuildCheck';

/**
 * Offers a reload once a newer build has been deployed than the one this tab is running.
 *
 * Checks on an interval and whenever the tab comes back into view -- the moment someone returns to
 * a tab left open across a deploy is exactly when they are about to act on stale code.
 *
 * It asks rather than reloading by itself: a reload in the middle of an order or invoice form would
 * throw away what was typed. The prompt stays until acted on, so it cannot be missed.
 */
function NewBuildNotice() {
  const [available, setAvailable] = useState(false);

  const check = useCallback(async () => {
    const deployed = await fetchDeployedBuild();
    if (isNewerBuild(runningBuild(), deployed)) {
      setAvailable(true);
    }
  }, []);

  useEffect(() => {
    // Unstamped (dev server, tests): nothing to compare, so do not poll at all.
    if (!runningBuild().built_at || available) {
      return undefined;
    }
    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [available, check]);

  return (
    <Snackbar open={available} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <SnackbarContent
        data-testid="new-build-notice"
        message="A new version of the app is available. Reload to get the latest changes."
        // A filled white button: the old text button (indigo on the dark snackbar) was easy to miss.
        action={
          <Button
            variant="contained"
            size="small"
            onClick={() => window.location.reload()}
            sx={{
              backgroundColor: 'common.white',
              color: 'primary.main',
              '&:hover': { backgroundColor: 'grey.200' },
            }}
          >
            Reload
          </Button>
        }
      />
    </Snackbar>
  );
}

export default NewBuildNotice;
